import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2, Landmark, Building2 } from "lucide-react";
import api, { handleApiError } from "@/lib/api";
import { toast } from "sonner";
import { BANK_LIST, isCompeFormat } from "@/data/banks";
import BankCombobox from "@/components/BankCombobox";
import { usePermission } from "@/hooks/usePermission";
import { useAuth } from "@/context/AuthContext";
import MfaBlockedBanner from "@/components/MfaBlockedBanner";

const BANK_TYPE_OPTIONS = [
  { value: "CC", label: "Conta Corrente" },
  { value: "CP", label: "Conta Poupança" },
];

const PIX_TYPE_OPTIONS = [
  { value: "cpf", label: "CPF" },
  { value: "cnpj", label: "CNPJ" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Celular" },
  { value: "random", label: "Chave Aleatória" },
];

const emptyForm = { razao_social: "", cnpj: "", bank_code: "", bank_agency: "", bank_agency_digit: "", bank_account: "", bank_account_digit: "", bank_type: "CC", pix_key: "", pix_key_type: "" };

export default function AdminCredores() {
  const { canEdit: canEditPerm } = usePermission("credores");
  const { user } = useAuth();
  // 2FA vencido: o backend recusa criar/editar/remover com 403 (FS-056).
  const mfaBlocked = !!user?.mfa_blocked;
  const canEdit = canEditPerm && !mfaBlocked;
  const [credores, setCredores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [saveError, setSaveError] = useState("");

  const fetchCredores = useCallback(async () => {
    try { const res = await api.get("/admin/credores"); setCredores(res.data); } catch { toast.error("Erro ao carregar credores"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCredores(); }, [fetchCredores]);

  const openCreate = () => { setForm({ ...emptyForm }); setEditingId(null); setSaveError(""); setDialogOpen(true); };
  const openEdit = (c) => {
    setForm({ razao_social: c.razao_social, cnpj: c.cnpj, bank_code: c.bank_code, bank_agency: c.bank_agency, bank_agency_digit: c.bank_agency_digit || "", bank_account: c.bank_account, bank_account_digit: c.bank_account_digit || "", bank_type: c.bank_type || "CC", pix_key: c.pix_key || "", pix_key_type: c.pix_key_type || "" });
    setEditingId(c.id);
    setSaveError("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.razao_social.trim() || !form.cnpj.trim()) { toast.error("Razão Social e CNPJ obrigatórios"); return; }
    if (!form.bank_code || !form.bank_agency.trim() || !form.bank_account.trim()) { toast.error("Dados bancários obrigatórios"); return; }
    if (!isCompeFormat(form.bank_code)) { toast.error("Banco: use o código COMPE de 3 dígitos (o ISPB não é aceito)"); return; }
    setSaving(true);
    setSaveError("");
    try {
      if (editingId) {
        await api.put(`/admin/credores/${editingId}`, form);
        toast.success(`${form.razao_social} atualizado`);
      } else {
        await api.post("/admin/credores", form);
        toast.success(`${form.razao_social} cadastrado`);
      }
      setDialogOpen(false);
      fetchCredores();
      // `handleApiError` e não `err.response?.data?.detail` (A-R3e-xv): agência e
      // conta ganharam `AfterValidator` no schema do credor
      // (`BankAgencyRequired`/`BankAccountRequired`), e o `detail` de um 422 do
      // pydantic é um ARRAY — o `||` caía direto no fallback e o operador via
      // "Erro ao salvar" sem saber qual campo nem por quê. O helper extrai o
      // `msg` dos validadores, que é a mensagem que ensina o formato.
    } catch (err) {
      const msg = handleApiError(err, "Erro ao salvar");
      setSaveError(msg);
      toast.error(msg);
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/credores/${deleteDialog}`);
      const credor = credores.find(c => c.id === deleteDialog);
      toast.success(`${credor?.razao_social || "Credor"} removido`);
      setDeleteDialog(null);
      fetchCredores();
    } catch (err) { toast.error(handleApiError(err, "Erro ao remover")); }
  };

  const bankName = (code) => BANK_LIST.find(b => b.code === code)?.name || code;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="loading-spinner w-8 h-8" /></div>;

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6 lg:space-y-8" data-testid="admin-credores">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Credores</h1>
          <p className="text-sm text-muted-foreground mt-2">Cadastre e gerencie os credores das operações de antecipação</p>
        </div>
        <Button data-testid="add-credor-btn" onClick={openCreate} disabled={!canEdit}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
          <Plus className="w-4 h-4 mr-2" /> Novo Credor
        </Button>
      </div>

      {mfaBlocked && <MfaBlockedBanner action="cadastrar ou editar credores" />}

      <Card className="border-white/5">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Razão Social</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">CNPJ</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Banco</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Agência</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Conta</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Tipo</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Empresas</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {credores.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Nenhum credor cadastrado</TableCell></TableRow>
              ) : credores.map((c) => (
                <TableRow key={c.id} data-testid={`credor-row-${c.id}`} className="border-white/5 hover:bg-white/[0.02]">
                  <TableCell className="font-medium">{c.razao_social}</TableCell>
                  <TableCell className="text-sm text-muted-foreground font-mono">{c.cnpj}</TableCell>
                  <TableCell className="text-sm">{c.bank_code} - {bankName(c.bank_code)}</TableCell>
                  <TableCell className="text-sm font-mono">{c.bank_agency}{c.bank_agency_digit ? `-${c.bank_agency_digit}` : ""}</TableCell>
                  <TableCell className="text-sm font-mono">{c.bank_account}{c.bank_account_digit ? `-${c.bank_account_digit}` : ""}</TableCell>
                  <TableCell><span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-500/15 text-blue-400">{c.bank_type}</span></TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 text-sm">
                      <Building2 className="w-3 h-3 text-muted-foreground" /> {c.companies_count || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" data-testid={`edit-credor-btn-${c.id}`}
                        onClick={() => openEdit(c)} disabled={!canEdit} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" data-testid={`delete-credor-btn-${c.id}`}
                        onClick={() => setDeleteDialog(c.id)} disabled={!canEdit} className="h-8 w-8 text-muted-foreground hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg border-white/10" data-testid="credor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Landmark className="w-5 h-5 text-blue-400" /> {editingId ? "Editar" : "Novo"} Credor</DialogTitle>
            <DialogDescription>Dados do credor para operações de antecipação</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Razão Social <span className="text-red-400">*</span></Label>
              <Input data-testid="credor-razao-social" value={form.razao_social}
                onChange={(e) => setForm({ ...form, razao_social: e.target.value })}
                className="bg-black/20 border-white/10" />
            </div>
            <div className="space-y-2">
              <Label>CNPJ <span className="text-red-400">*</span></Label>
              <Input data-testid="credor-cnpj" value={form.cnpj}
                onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                placeholder="00.000.000/0000-00"
                className="bg-black/20 border-white/10" />
            </div>
            <div className="space-y-2">
              <Label>Banco <span className="text-red-400">*</span></Label>
              <BankCombobox value={form.bank_code} onChange={(v) => setForm({ ...form, bank_code: v })} testId="credor-bank-code" placeholder="Selecione..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Agência <span className="text-red-400">*</span></Label>
                <Input data-testid="credor-agency" value={form.bank_agency}
                  onChange={(e) => setForm({ ...form, bank_agency: e.target.value })}
                  placeholder="0001" className="bg-black/20 border-white/10" />
              </div>
              <div className="space-y-2">
                <Label>Dígito Agência</Label>
                <Input data-testid="credor-agency-digit" value={form.bank_agency_digit}
                  onChange={(e) => setForm({ ...form, bank_agency_digit: e.target.value })}
                  placeholder="0" maxLength={2} className="bg-black/20 border-white/10" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Conta <span className="text-red-400">*</span></Label>
                <Input data-testid="credor-account" value={form.bank_account}
                  onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                  placeholder="123456" className="bg-black/20 border-white/10" />
              </div>
              <div className="space-y-2">
                <Label>Dígito Conta</Label>
                <Input data-testid="credor-account-digit" value={form.bank_account_digit}
                  onChange={(e) => setForm({ ...form, bank_account_digit: e.target.value })}
                  placeholder="7" maxLength={2} className="bg-black/20 border-white/10" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tipo de Conta <span className="text-red-400">*</span></Label>
              <Select value={form.bank_type} onValueChange={(v) => setForm({ ...form, bank_type: v })}>
                <SelectTrigger data-testid="credor-bank-type" className="bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BANK_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tipo de Chave PIX</Label>
                <Select value={form.pix_key_type} onValueChange={(v) => setForm({ ...form, pix_key_type: v })}>
                  <SelectTrigger data-testid="credor-pix-type" className="bg-black/20 border-white/10"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {PIX_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Chave PIX</Label>
                <Input data-testid="credor-pix-key" value={form.pix_key}
                  onChange={(e) => setForm({ ...form, pix_key: e.target.value })}
                  placeholder="email@pix.com, CPF ou chave" className="bg-black/20 border-white/10" />
              </div>
            </div>
          </div>
          {saveError && (
            <p role="alert" data-testid="credor-save-error"
              className="text-sm text-red-400 p-3 bg-red-500/5 border border-red-500/20 rounded-lg">
              {saveError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-white/10">Cancelar</Button>
            <Button data-testid="save-credor-btn" onClick={handleSave} disabled={saving || mfaBlocked}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
              {saving ? <div className="loading-spinner" /> : editingId ? "Salvar" : "Cadastrar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-sm border-white/10" data-testid="delete-credor-dialog">
          <DialogHeader>
            <DialogTitle>Excluir Credor</DialogTitle>
            <DialogDescription>O credor será removido. Empresas vinculadas precisarão de um novo credor.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="border-white/10">Cancelar</Button>
            <Button data-testid="confirm-delete-credor-btn" onClick={handleDelete} disabled={mfaBlocked}
              className="bg-red-600 hover:bg-red-700 text-white">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
